import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Selections } from 'src/app/model/out-fit';
import { HomeService } from '../home/services/home.service';
import {
  validateShirt,
  validateShort,
  validateSocks,
  validateMaterials,
  ValidationResult,
} from 'src/app/validations/customization/customization.validation';
import html2canvas from 'html2canvas';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-customization',
  templateUrl: './customization.component.html',
  styleUrls: ['./customization.component.css'],
})
export class CustomizationComponent implements AfterViewInit, OnDestroy {
  @ViewChild('svgOutFit') svgOutFit!: ElementRef;

  step = 1;
  // keep your existing selections/colors...

  /** track last validation for showing messages */
  lastValidation: ValidationResult | null = null;
  /** show errors only after the user tries to advance */
  triedNextOnStep1 = false;
  triedNextOnStep2 = false;
  triedNextOnStep3 = false;

  selections: Selections = this.defaultSelections();
  colors = [
    '#231F20',
    '#FFFFFF',
    '#ED1C24',
    '#051BC2',
    '#C79D3B',
    '#D2D3D5',
    '#032557',
    '#2E1D6C',
    '#00A651',
    '#00AEEF',
    '#59595B',
    '#700203',
    '#DBE700',
    '#F58320',
    '#4D3E98',
    '#EC008C',
  ];

  constructor(
    private http: HttpClient,
    private homeService: HomeService,
    private translate: TranslateService,
  ) {}

  ngAfterViewInit() {
    this.adjustLogoPositions();
    window.addEventListener('resize', this.adjustLogoPositions.bind(this));
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.adjustLogoPositions.bind(this));
  }

  private defaultSelections(): Selections {
    return {
      shirt: {
        jersyType: 'raglan',
        collarColor: '',
        FrontCollarColor: '',
        collarType: 'C1',
        shoulderColor: '',
        bodyColor: '',
        isSleeve: true,
        sleeveColor: '',
        logoColor: '',
        shoulderLogoColor: '',
        logoPath: '',
        logoFile: '',
      },

      short: { color: '', logoColor: '', logoPath: '' },
      socks: { topColor: '', logoColor: '', bottomColor: '', logoPath: '' },
      sizes: [],
      materials: [],
    };
  }

  setData(step: string, part: string, value: any) {
    if (part === 'sizes' || part === 'materials') {
      this.selections[part] = value;
      return;
    }

    (this.selections as any)[step][part] = value;

    if (step === 'shirt') {
      this.applyShirtFit();
      if (part === 'logoPath') {
        this.selections.short.logoPath = value;
        this.selections.socks.logoPath = value;
      }
      if (part === 'collarType') this.changeCollarType();

      // 🔁 live-validate step 1 if user already tried
      if (this.triedNextOnStep1) {
        const res = validateShirt(this.selections.shirt);
        this.lastValidation = res;
        if (res.valid) this.triedNextOnStep1 = false; // unlock button
      }
    } else if (step === 'short') {
      this.applyShortFit();

      // 🔁 live-validate step 2 if user already tried
      if (this.triedNextOnStep2) {
        const res = validateShort(this.selections.short);
        this.lastValidation = res;
        if (res.valid) this.triedNextOnStep2 = false; // unlock button
      }
    } else if (step === 'socks') {
      this.applySocksFit();

      // 🔁 live-validate step 3 if user already tried
      if (this.triedNextOnStep3) {
        const res = validateSocks(this.selections.socks);
        this.lastValidation = res;
        if (res.valid) this.triedNextOnStep3 = false; // unlock button
      }
    }
  }

  get isStep1Valid(): boolean {
    return validateShirt(this.selections.shirt).valid;
  }
  get isStep2Valid(): boolean {
    return validateShort(this.selections.short).valid;
  }
  get isStep3Valid(): boolean {
    return validateSocks(this.selections.socks).valid;
  }

  nextStep() {
    let res: ValidationResult;
    if (this.step === 1) {
      res = validateShirt(this.selections.shirt);
      this.lastValidation = res;
      this.triedNextOnStep1 = true;
    } else if (this.step === 2) {
      res = validateShort(this.selections.short);
      this.lastValidation = res;
      this.triedNextOnStep2 = true;
    } else if (this.step === 3) {
      res = validateSocks(this.selections.socks);
      this.lastValidation = res;
      this.triedNextOnStep3 = true;
    }

    if (this.lastValidation && !this.lastValidation.valid) {
      alert(this.translate.instant('CUSTOMIZATION.ALERTS.CORRECT_ERRORS'));
      this.scrollToFirstError(this.lastValidation.errorKeys);
      return; // STOP
    }

    this.triedNextOnStep1 = false;
    this.triedNextOnStep2 = false;
    this.triedNextOnStep3 = false;

    window.scrollTo(0, 0);
    if (this.step < 5) {
      this.step++;
      setTimeout(() => this.adjustLogoPositions(), 0);
    }
  }

  private scrollToFirstError(errors: string[]) {
    if (errors.length === 0) return;
    const firstErrorKey = errors[0];
    const errorFieldMap: { [key: string]: string } = {
      'SHIRT.COLLAR_COLOR_REQUIRED': '#collarColor',
      'SHIRT.SHOULDER_COLOR_REQUIRED': '#shoulderColor',
      'SHIRT.BODY_COLOR_REQUIRED': '#bodyColor',
      'SHIRT.SLEEVE_COLOR_REQUIRED': '#sleeveColor',
      'SHIRT.LOGO_COLOR_REQUIRED': '#logoColor',
      'SHIRT.SHOULDER_LOGO_COLOR_REQUIRED': '#shoulderLogoColor',
      'SOCKS.TOP_COLOR_REQUIRED': '#topColor',
      'SOCKS.LOGO_COLOR_REQUIRED': '#logoColor',
      'SOCKS.BOTTOM_COLOR_REQUIRED': '#bottomColor',
    };

    const selector = errorFieldMap[firstErrorKey];
    if (selector) {
      const element = document.querySelector(selector);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  previousStep() {
    window.scrollTo(0, 0);
    if (this.step > 1) this.step--;
  }

  reset() {
    window.scrollTo(0, 0);
    this.selections = this.defaultSelections();
    this.applyShirtFit();
    this.applyShortFit();
    this.applySocksFit();
    this.changeCollarType();
  }

  applyShortFit() {
    this.setFill('#short', this.selections.short.color);
    this.setFill('#short-logo', this.selections.short.logoColor);
  }

  applyShirtFit() {
    this.setFill(
      '#collar, #collarType svg path:not(#front_collar)',
      this.selections.shirt.collarColor,
    );
    this.setFill('#front_collar', this.selections.shirt.FrontCollarColor);
    this.setFill('#body', this.selections.shirt.bodyColor);
    this.setFill('#shoulder', this.selections.shirt.shoulderColor);
    this.setFill('#sleeve', this.selections.shirt.sleeveColor);
    this.setFill('#logo', this.selections.shirt.logoColor);
    this.setFill('#shoulder_logo', this.selections.shirt.shoulderLogoColor);
    this.toggleDisplay('#sleeve', this.selections.shirt.isSleeve);
  }

  applySocksFit() {
    this.setFill('#topSocks', this.selections.socks.topColor);
    this.setFill('#socksLogo', this.selections.socks.logoColor);
    this.setFill('#socksBottomColor', this.selections.socks.bottomColor);
  }

  private setFill(selector: string, color: string) {
    const paths = this.svgOutFit?.nativeElement.querySelectorAll(selector);
    paths?.forEach((el: Element) => el.setAttribute('fill', color));
  }

  private toggleDisplay(selector: string, show: boolean) {
    const elements = this.svgOutFit?.nativeElement.querySelectorAll(selector);
    elements?.forEach(
      (el: Element) =>
        ((el as HTMLElement).style.display = show ? 'block' : 'none'),
    );
  }

  async changeCollarType() {
    try {
      let collarSVG = await this.http
        .get(`assets/img/customize/${this.selections.shirt.collarType}.svg`, {
          responseType: 'text',
        })
        .toPromise();
      const collarEl =
        this.svgOutFit.nativeElement.querySelector('#collarType');

      if (collarEl && collarSVG) {
        if (this.selections.shirt.jersyType === 'basic') {
          console.log(this.selections.shirt.jersyType);
          if (
            this.selections.shirt.collarType == 'C1' ||
            this.selections.shirt.collarType == 'C2'
          ) {
            collarSVG = collarSVG.replace(
              /translate\((\d+),\s*(\d+)\)/,
              (match, x, y) => `translate(${parseInt(x) - 5}, ${y})`,
            );
            collarSVG = collarSVG.replace(
              /scale\(([\d.]+)\)/,
              (match, x) => `scale(.78)`,
            );
          } else if (
            this.selections.shirt.collarType == 'C10' ||
            this.selections.shirt.collarType == 'C11' ||
            this.selections.shirt.collarType == 'C13' ||
            this.selections.shirt.collarType == 'C14' ||
            this.selections.shirt.collarType == 'C14' ||
            this.selections.shirt.collarType == 'C17' ||
            this.selections.shirt.collarType == 'C3'
          ) {
            collarSVG = collarSVG.replace(
              /translate\((\d+),\s*(\d+)\)/,
              (match, x, y) =>
                `translate(${parseInt(x) - 8}, ${parseInt(y) + 2})`,
            );
            collarSVG = collarSVG.replace(
              /scale\(([\d.]+)\)/,
              (match, x) => `scale(.7)`,
            );
          } else if (
            this.selections.shirt.collarType == 'C4' ||
            this.selections.shirt.collarType == 'C6' ||
            this.selections.shirt.collarType == 'C8' ||
            this.selections.shirt.collarType == 'C9' ||
            this.selections.shirt.collarType == 'C7' ||
            this.selections.shirt.collarType == 'C5'
          ) {
            collarSVG = collarSVG.replace(
              /translate\((\d+),\s*(\d+)\)/,
              (match, x, y) =>
                `translate(${parseInt(x) - 3}, ${parseInt(y) + 2})`,
            );
            collarSVG = collarSVG.replace(
              /scale\(([\d.]+)\)/,
              (match, x) => `scale(.68)`,
            );
          } else if (
            this.selections.shirt.collarType == 'C15' ||
            this.selections.shirt.collarType == 'C16' ||
            this.selections.shirt.collarType == 'C12' ||
            this.selections.shirt.collarType == 'C19'
          ) {
            // Wrap SVG content in a <g> with transform
            collarSVG = collarSVG.replace(
              /(<svg[^>]*>)(.*?)(<\/svg>)/s,
              '$1<g transform="translate(7, 0) scale(0.85)">$2</g>$3',
            );
          }
        } else {
          if (
            this.selections.shirt.collarType == 'C4' ||
            this.selections.shirt.collarType == 'C7' ||
            this.selections.shirt.collarType == 'C6' ||
            this.selections.shirt.collarType == 'C5' ||
            this.selections.shirt.collarType == 'C8' ||
            this.selections.shirt.collarType == 'C9' ||
            this.selections.shirt.collarType == 'C10' ||
            this.selections.shirt.collarType == 'C11' ||
            this.selections.shirt.collarType == 'C12' ||
            this.selections.shirt.collarType == 'C13'
          ) {
            collarSVG = collarSVG.replace(
              /translate\((\d+),\s*(\d+)\)/,
              (match, x, y) => `translate(${x}, ${parseInt(y) + 7})`,
            );
          } else if (this.selections.shirt.collarType == 'C3') {
            collarSVG = collarSVG.replace(
              /translate\((\d+),\s*(\d+)\)/,
              (match, x, y) => `translate(${x}, ${parseInt(y) + 6})`,
            );
          } else if (this.selections.shirt.collarType == 'C14') {
            collarSVG = collarSVG.replace(
              /translate\((\d+),\s*(\d+)\)/,
              (match, x, y) =>
                `translate(${parseInt(x) - 7}, ${parseInt(y) + 7})`,
            );
            collarSVG = collarSVG.replace(
              /scale\(([\d.]+)\)/,
              (match, x) => `scale(.92)`,
            );
          }
          collarSVG = collarSVG.replace(/height="[^"]*"/, 'height="67"');
        }
        collarEl.innerHTML = collarSVG;
        this.setFill(
          '#collar, #collarType svg path:not(#front_collar)',
          this.selections.shirt.collarColor,
        );
        this.setFill('#front_collar', this.selections.shirt.FrontCollarColor);
      }
    } catch (err) {
      console.error('Collar change error:', err);
    }
  }

  uploudLogo(e: any) {
    const file = typeof e === 'string' ? null : e.target.files[0];
    const url = file ? URL.createObjectURL(file) : e;
    if (file) this.selections.shirt.logoFile = file;
    this.selections.shirt.logoPath = url;

    setTimeout(() => {
      const svg = this.svgOutFit?.nativeElement.querySelector('svg');
      const logo = svg?.querySelector('#uploaded-logo');
      if (logo) logo.setAttribute('href', url);
    }, 100);
  }

  private async captureAndUpload(): Promise<string | null> {
    const container = this.svgOutFit.nativeElement.closest('.svg-container');
    if (!container) return null;

    const canvas = await html2canvas(container, {
      backgroundColor: 'white',
      useCORS: true,
    });
    const base64Image = canvas
      .toDataURL('image/png')
      .replace(/^data:image\/png;base64,/, '');

    const formData = new FormData();
    formData.append('image', base64Image);

    const response = await fetch(
      'https://api.imgbb.com/1/upload?key=18ce311c9a214c5e827b40c0ba2d09b1',
      {
        method: 'POST',
        body: formData,
      },
    );

    const result = await response.json();
    return result?.data?.url || null;
  }

  async downloadColoredSvg() {
    const container = this.svgOutFit.nativeElement.closest('.svg-container');
    if (!container) return;
    const canvas = await html2canvas(container, {
      backgroundColor: 'white',
      useCORS: true,
    });
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = 'outfit-colored.png';
    a.click();
  }

  private buildMessage(imageUrl: string): string {
    let msg = `🧥 Outfit Image:\n${imageUrl}`;

    const sizes = this.selections.sizes
      .filter((s) => s.count > 0)
      .map((s) => `• ${s.size}: ${s.count}`)
      .join('\n');
    if (sizes) msg += `\n📏 Sizes:\n${sizes}`;

    const materials = this.selections.materials
      .filter((m) => m.selectedMaterial && m.selectedFabricType)
      .map(
        (m) =>
          `• ${m.label}\n  - Fabric Type: ${m.selectedFabricType}\n  - Material: ${m.selectedMaterial!.name}\n  - Details: ${m.selectedMaterial!.details}`,
      )
      .join('\n\n');
    if (materials) msg += `\n🧵 Materials:\n${materials}`;

    return msg;
  }

  async sendColoredSvgToWhatsApp() {
    const materialsValidation = validateMaterials(this.selections.materials);
    if (!materialsValidation.valid) {
      alert(this.translate.instant('CUSTOMIZATION.ALERTS.SELECT_MATERIALS'));
      return;
    }

    const url = await this.captureAndUpload();
    if (!url) return;
    const msg = this.buildMessage(url);
    this.homeService.getConfig('en').subscribe((res) => {
      let whatsappUrl = res.whatsappUrl + `?text=${encodeURIComponent(msg)}`;
      window.open(whatsappUrl, '_blank');
    });
  }

  async sendColoredSvgToEmail() {
    const materialsValidation = validateMaterials(this.selections.materials);
    if (!materialsValidation.valid) {
      alert(this.translate.instant('CUSTOMIZATION.ALERTS.SELECT_MATERIALS'));
      return;
    }

    const url = await this.captureAndUpload();
    if (!url) return;
    const msg = this.buildMessage(url);
    this.homeService.getConfig('en').subscribe((res) => {
      let email = `mailto:`;
      res.email +
        `?subject=${encodeURIComponent(
          'New Outfit Order',
        )}&body=${encodeURIComponent(msg)}`;
      window.open(email, '_blank');
    });
  }

  adjustLogoPositions() {
    const baseWidth = 400;
    const baseHeight = 600;
    const baseLogoWidth = 24;

    const currentWidth = this.svgOutFit?.nativeElement.offsetWidth || baseWidth;
    const currentHeight =
      this.svgOutFit?.nativeElement.offsetHeight || baseHeight;

    const scaleX = currentWidth / baseWidth;
    const scaleY = currentHeight / baseHeight;

    const root = this.svgOutFit.nativeElement.closest('.svg-container');

    if (root) {
      root.style.setProperty('--main-logo-top', `${70 * scaleY}px`);
      root.style.setProperty('--main-logo-left', `${107 * scaleX}px`);
      root.style.setProperty('--short-logo-top', `${346 * scaleY}px`);
      root.style.setProperty('--short-logo-left', `${143 * scaleX}px`);
      root.style.setProperty('--logo-width', `${baseLogoWidth * scaleX}px`);
    }
  }
}
